// --- internal
import { useBrand, useDataLayer, useQueryParams, useRoutingEngine } from "../";

// --- utils
import { get } from "lodash-es";

// --- types
import type { Router, RouteLocation, RouteLocationGeneric } from "vue-router";
import { type UIRouteOptions } from "../brand/types";
import { decorateRoutes, hasRouteChanged } from "./utils";

// -----------------------------------------------------------------------------

/**
 * Initialises routing within the application by integrating the routing engine with the provided Vue Router instance.
 * the function sets up route guarding and decoration to manage navigation flows and apply brand-specific UI schemas.
 * @param router  - The Vue Router instance to integrate with the routing engine.
 */
export const useRouting = (router: Router): void => {
  const { init, guard, switchFunnel } = useRoutingEngine();
  const { dataLayer } = useDataLayer();

  // Initialise our engine with the given router
  init(router);

  // --- methods

  /**
   * Guard the route, using the routing engine to determine if navigation should proceed
   * @param route
   */
  async function guardRoute(route: RouteLocation) {
    if (route?.query?.funnel) {
      const { consumeParam } = useQueryParams(route as RouteLocationGeneric);
      const funnel = consumeParam("funnel");
      await switchFunnel(funnel.toString(), route);
    }
    const target = await guard(route);

    // Only redirect if target exists and is meaningfully different from current route
    if (target && hasRouteChanged(route, target)) {
      return target;
    }

    return;
  }

  // ---------------------------------------------------------------------------

  /**
   * Initial route decoration and guarding on app load
   * NB: once the router is ready then need to force check the current route
   *     This is because on load the vue router resolves the route before the engine is ready
   */
  router.isReady().then(async () => {
    await decorateRoutes(router.getRoutes());
    // NB: Apps call registerOverlayRoutes(router, overlays) in their router setup
    // before this point. No fallback call needed here.
    const target = await guardRoute(router.currentRoute.value);
    if (target) {
      await router.replace(target);
    }
  });

  /**
   * Guard the route before each navigation with the routing engine
   * but only if the route name has changed
   */
  router.beforeEach(async to => guardRoute(to));

  /**
   * After each route navigation, push a page_view event to the data layer for analytics
   */
  router.afterEach((to, from) => {
    dataLayer({ event: "page_view" })
      .withPage({
        to,
        from
      })
      .push(false);
  });
};
